import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import CTASection from "@/components/public/CTASection";
import ReviewCard from "@/components/public/ReviewCard";
import DevisForm from "@/components/public/DevisForm";
import ServiceCard from "@/components/public/ServiceCard";
import { Building2, TrendingDown, Users, ShieldCheck, Calculator, Truck } from "lucide-react";
import SeoHead from "@/components/public/SeoHead";
import Breadcrumb from "@/components/public/Breadcrumb";
import FadeIn from "@/components/public/FadeIn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = "/images/hero-contrats-entreprises.jpg";

function CostSimulator() {
  const [surface, setSurface] = useState(200);
  const [freq, setFreq] = useState("hebdomadaire");
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState("");

  useEffect(() => {
    axios.get(`${API}/zones-deplacement`).then(r => {
      setZones(r.data);
      if (r.data.length > 0) setSelectedZone(r.data[0].zone);
    });
  }, []);

  const degradationCost = Math.round(surface * (freq === "quotidien" ? 15 : freq === "hebdomadaire" ? 25 : 45));
  const neCost = Math.round(surface * (freq === "quotidien" ? 3 : freq === "hebdomadaire" ? 1.5 : 0.8));
  const zoneObj = zones.find(z => z.zone === selectedZone);
  const deplacementCost = zoneObj ? zoneObj.prix : 0;

  return (
    <div className="bg-[#F4F6F8] border border-[#062133]/15 rounded-lg p-6 sm:p-8" data-testid="b2b-cost-simulator">
      <div className="flex items-center gap-2 mb-4">
        <Calculator className="w-5 h-5 text-[#336DBF]" />
        <h3 className="text-xl font-semibold text-[#062133]">Combien vous coûte un mauvais nettoyage ?</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="text-sm font-medium text-[#062133] mb-1.5 block">Surface (m2)</label>
          <input type="range" min={50} max={2000} step={50} value={surface} onChange={e => setSurface(parseInt(e.target.value))} className="w-full accent-[#336DBF]" data-testid="sim-surface" />
          <p className="text-center text-lg font-semibold text-[#336DBF] mt-1">{surface} m2</p>
        </div>
        <div>
          <label className="text-sm font-medium text-[#062133] mb-1.5 block">Frequence actuelle</label>
          <select value={freq} onChange={e => setFreq(e.target.value)} className="w-full border border-[#062133]/15 rounded-md px-3 py-2 text-sm" data-testid="sim-freq">
            <option value="quotidien">Quotidien</option>
            <option value="hebdomadaire">Hebdomadaire</option>
            <option value="mensuel">Mensuel</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-[#062133] mb-1.5 block">Zone de déplacement</label>
          <select value={selectedZone} onChange={e => setSelectedZone(e.target.value)} className="w-full border border-[#062133]/15 rounded-md px-3 py-2 text-sm" data-testid="sim-zone">
            {zones.map(z => (
              <option key={z.zone} value={z.zone}>{z.zone}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-xs font-bold uppercase tracking-wider text-red-600 mb-1">Coût nettoyage inadapté</p>
          <p className="text-2xl font-bold text-red-700">{degradationCost} CHF/an</p>
          <p className="text-xs text-red-500 mt-1">en dégradation estimée</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-600 mb-1">Investissement Nettoyage Expert</p>
          <p className="text-2xl font-bold text-emerald-700">dès {neCost} CHF/mois</p>
          <p className="text-xs text-emerald-500 mt-1">pour un résultat professionnel</p>
        </div>
      </div>
      <div className="mt-6 text-center">
        <Link to="/contact" className="inline-flex items-center justify-center px-6 py-3 bg-[#336DBF] text-white font-semibold rounded-md hover:bg-[#2A5A9E] transition-colors" data-testid="sim-devis-cta">
          Recevoir une proposition personnalisée
        </Link>
      </div>
    </div>
  );
}

export default function ContratsEntreprisesPage() {
  const [services, setServices] = useState([]);
  const [avis, setAvis] = useState([]);

  useEffect(() => {
    Promise.all([axios.get(`${API}/services`), axios.get(`${API}/avis`)]).then(([s, a]) => {
      setServices(s.data.filter(sv => sv.type_client === "B2B"));
      setAvis(a.data.filter(av => av.type_client === "entreprise"));
    });
  }, []);

  return (
    <div data-testid="contrats-page">
      <SeoHead
        title="Contrats nettoyage entreprises Suisse romande"
        description="Contrats de nettoyage sur mesure pour entreprises en Suisse romande. Bureaux, hôtels, régies. Budget prévisible. Devis gratuit. Nettoyage Expert."
        path="/contrats-entreprises"
        breadcrumbs={[{ name: "Accueil", url: "/" }, { name: "Contrats entreprises" }]}
      />
      <Breadcrumb items={[{ name: "Accueil", url: "/" }, { name: "Contrats entreprises" }]} />
      {/* Hero */}
      <section className="relative py-16 sm:py-24">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#062133]/90 via-[#062133]/75 to-[#062133]/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight">
            Contrats de nettoyage pour entreprises en Suisse romande
          </h1>
          <p className="mt-4 text-white/80 text-lg max-w-2xl">
            Un partenaire fiable pour la propreté de vos locaux. Contrats sur mesure, équipes dédiées, résultats garantis.
          </p>
          <Link to="/contact" className="mt-6 inline-flex items-center justify-center px-8 py-3.5 bg-[#336DBF] text-white font-semibold rounded-md hover:bg-[#2A5A9E] transition-colors" data-testid="contrats-hero-cta">
            Demander une proposition
          </Link>
        </div>
      </section>

      {/* Why outsource */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Avantages</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight">Pourquoi externaliser votre nettoyage</h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
            {[
              { icon: TrendingDown, title: "Réduction des coûts", desc: "Pas de charges salariales, pas de matériel à acheter, pas de formation à financer. Un budget maîtrisé et prévisible." },
              { icon: ShieldCheck, title: "Fiabilité garantie", desc: "Contrat, SLA, remplacement en cas d'absence. Vous ne gérez plus les absences et les congés." },
              { icon: Users, title: "Expertise professionnelle", desc: "Équipes formées, produits certifiés, matériel professionnel. Un niveau de propreté que le personnel interne ne peut pas atteindre." },
            ].map((item, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div className="p-6 border border-[#062133]/10 rounded-lg">
                  <item.icon className="w-8 h-8 text-[#336DBF] mb-4" />
                  <h3 className="font-semibold text-lg text-[#062133] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#062133]/60 leading-relaxed">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Common mistakes */}
      <section className="py-16 sm:py-24 bg-[#F4F6F8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Attention</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-6">Les erreurs qui coutent cher aux entreprises</h2>
            <div className="space-y-4">
              {[
                "Choisir le prestataire le moins cher : un nettoyage bâclé coûte plus cher en dégradation et en rotation du prestataire.",
                "Compter sur le personnel interne : sans formation ni matériel adapté, le résultat est médiocre et le coût réel est supérieur.",
                "Ne pas avoir de contrat clair : sans SLA ni indicateurs de qualité, vous n'avez aucun levier en cas de problème.",
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-3 bg-white rounded-lg p-4 border border-[#062133]/10">
                  <span className="text-red-500 font-bold text-sm shrink-0 mt-0.5">#{i + 1}</span>
                  <p className="text-[#062133]/60 text-sm leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* B2B Cost Simulator */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <CostSimulator />
          </FadeIn>
        </div>
      </section>

      <CTASection title="Simplifiez la gestion de votre nettoyage" subtitle="Un seul interlocuteur, une facturation transparente, un résultat garanti." buttonText="Recevoir une proposition" />

      {/* Fin de bail mention */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
              <h3 className="font-semibold text-[#062133] mb-2">Régies immobilières : nettoyage fin de bail</h3>
              <p className="text-sm text-[#062133]/70 leading-relaxed">
                Vous gérez des remises locatives ? Notre service de{" "}
                <a href="/services/nettoyage-fin-de-bail" className="text-[#336DBF] font-medium hover:underline">nettoyage fin de bail</a>{" "}
                garantit un résultat conforme aux exigences des régies romandes. Un seul interlocuteur pour toutes vos remises, des interventions planifiées et un résultat constant.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* B2B Services */}
      <section className="py-16 sm:py-24 bg-[#F4F6F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Nos contrats</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-8">Nos contrats sur mesure</h2>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map(s => <ServiceCard key={s.slug} service={s} />)}
          </div>
        </div>
      </section>

      {/* B2B Reviews */}
      {avis.length > 0 && (
        <section className="py-16 sm:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight mb-8">Ils nous font confiance</h2>
            </FadeIn>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {avis.slice(0, 3).map((a, i) => <FadeIn key={a.id || i} delay={i * 100}><ReviewCard avis={a} /></FadeIn>)}
            </div>
          </div>
        </section>
      )}

      {/* DevisForm */}
      <section className="py-16 sm:py-24 bg-[#F4F6F8]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="bg-[#336DBF]/5 border border-[#336DBF]/15 rounded-lg p-6 mb-10">
              <h3 className="font-semibold text-[#062133] mb-2">Nos outils pour votre suivi</h3>
              <p className="text-sm text-[#062133]/70 leading-relaxed">
                Suivi des contrats, relances (e-mail, WhatsApp, bot vocal), devis et factures en ligne : tout est piloté dans{" "}
                <a href="https://www.loomy-crm.com" target="_blank" rel="noopener" className="text-[#336DBF] font-medium hover:underline">Loomy CRM</a>.
              </p>
            </div>
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight">Demandez votre proposition</h2>
              <p className="mt-2 text-[#062133]/60">Audit gratuit de vos locaux et proposition personnalisée</p>
            </div>
            <div className="bg-white border border-[#062133]/10 rounded-lg p-6 sm:p-8 shadow-sm">
              <DevisForm source="direct" prefilledTypeClient="entreprise" />
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
